import React, { useState, useEffect } from "react";
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Button,
  Input,
  Label,
  FormGroup,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import Request from "../../../../helpers/request";
import * as url from "../../../../helpers/url_helper";
import DeviceControl from "./Common/DeviceControl";
import { useSelector } from "react-redux";

type TriggerType = "device" | "schedule" | "location" | "environment";

type CategoryKey = "switch" | "aircurtain" | "airdriver" | "airlock";

type View =
  | "main"
  | "triggerType"
  | "actionType"
  | "deviceList"
  | "schedule"
  // | "location"
  // | "environment"
  | "notification"
  | "delay"
  | "scene"
  | "effectiveTime";

const CATEGORY_KEYS: CategoryKey[] = [
  "switch",
  "aircurtain",
  "airdriver",
  "airlock",
];

const CATEGORY_LABELS: Record<CategoryKey, string> = {
  switch: "Switch",
  aircurtain: "Air Curtain",
  airdriver: "Air Driver",
  airlock: "Air Lock",
};

const CATEGORY_ENDPOINTS: Record<CategoryKey, string> = {
  switch: url.GET_CHIPS_LIST,
  aircurtain: url.GET_CURTAIN_LIST,
  airdriver: url.GET_GATEWAY_LIST,
  airlock: url.GET_LOCK_LIST,
};

const AIR_DRIVER_DEFAULT_BRIGHTNESS = 100;
const AIR_DRIVER_DEFAULT_TEMP = 4000;

const VIEW_TITLE: Record<View, string> = {
  main: "Create Automation",
  triggerType: "Add a trigger condition",
  actionType: "Add an action",
  deviceList: "Select Devices",
  schedule: "Schedule trigger",
  // location: "Arrive / Leave trigger",
  // environment: "Outdoor environment trigger",
  notification: "Notification action",
  delay: "Delay action",
  scene: "Scene action",
  effectiveTime: "Effective time period",
};

// repeatType: 0 = Once, 1 = Daily, 2 = Weekdays, 3 = Weekends, 4 = Custom
const REPEAT_TYPE_LABELS: Record<number, string> = {
  0: "Once",
  1: "Daily",
  2: "Weekdays",
  3: "Weekends",
  4: "Custom",
};

const WEEK_DAYS = [
  { number: 0, short: "Sun" },
  { number: 1, short: "Mon" },
  { number: 2, short: "Tue" },
  { number: 3, short: "Wed" },
  { number: 4, short: "Thu" },
  { number: 5, short: "Fri" },
  { number: 6, short: "Sat" },
];

const WEEKDAY_NUMBERS = [1, 2, 3, 4, 5];
const WEEKEND_NUMBERS = [0, 6];
const ALL_DAY_NUMBERS = [0, 1, 2, 3, 4, 5, 6];

interface DeviceRow {
  id: string;
  name: string;
  roomName?: string;
  isOnline?: boolean;
  switches?: any[]; // present for "switch" chip rows
  drivers?: any[]; // present for "airdriver" gateway rows
  raw: any;
  modelType: string;
  type: string;
  category: CategoryKey;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userId?: any;
  selectedPremise?: any;
  onSaved: () => void;
  editData?: any; // pass the automation row to edit; omit/null = create mode
}

interface SelectedDeviceItem {
  itemId: string;
  deviceId: string;
  device: DeviceRow;
  category: CategoryKey;
  displayName?:string;
  state: any;
}

const normalizeDevice = (row: any, category: CategoryKey): DeviceRow => ({
  id: row.id || row._id || row.slave,
  name: row.name || row.displayName || row.slave,
  roomName: row.roomName,
  isOnline: row.isOnline,
  switches: row.switches,
  drivers: row.drivers,
  raw: row,
  modelType: row.modelType,
  type: row.type,
  category,
});

const buildCurtainExecuteValue = (button: string, dimmerValue: string) =>
  `${button}${dimmerValue}1`;

// schedule time
const getCurrentTimeString = () => {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const buildGroupId = (
  category: CategoryKey,
  roomName: string,
  modelType: string,
) => `${category}-${roomName}-${modelType}`;

const groupDevicesByRoomAndModel = (devices: DeviceRow[]) => {
  return devices.reduce((rooms, device) => {
    const roomName =
      device.roomName?.trim() || device?.name?.trim() || "No Room";
    const modelType =
      device.modelType?.trim() || device?.type?.trim() || "Unknown";
    const uniqueKey = `${modelType}_${device.id}`;

    if (!rooms[roomName]) rooms[roomName] = {};
    if (!rooms[roomName][uniqueKey]) rooms[roomName][uniqueKey] = [];

    rooms[roomName][uniqueKey].push(device);

    return rooms;
  }, {} as Record<string, Record<string, DeviceRow[]>>);
};

const FormGroupBlock = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <FormGroup className="mb-0">
    <Label className="fw-semibold" style={{ fontSize: 13 }}>
      {label}
    </Label>
    {children}
  </FormGroup>
);


const formatSecondsToMMSS = (totalSeconds: number) => {
  const total = Math.max(0, Number(totalSeconds) || 0);
  const mm = String(Math.floor(total / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

const hydrateConditionFromStored = (c: any) => {
  switch (c?.type) {
    case "device":
      return {
        type: "device",
        chipId: c.chipId,
        chipSwitchId: c.chipSwitchId,
        slave: c.slave,
        button: c.button,
        executeValue: c.executeValue,
        label: c.label,
      };
    case "schedule":
      return {
        type: "schedule",
        scheduleType: c.scheduleType ?? 0,
        time: c.time,
        delay: c.delay ?? 0,
        label: c.label,
      };
    case "zone":
      return {
        type: "location",
        direction: c.subType === "outside" ? "leave" : "arrive",
        subType: c.subType || "inside",
        location: c.location || "0,0",
        locationDisplayName: c.locationDisplayName || "",
        label: c.label,
      };
    case "environment":
      return {
        type: "environment",
        parameter: c.parameter,
        operator: c.operator,
        value: c.value,
        label: c.label,
      };
    default:
      return c;
  }
};

const hydrateActionFromStored = (a: any) => {
  switch (a?.type) {
    case "chip":
      return {
        type: "device",
        chipId: a.chipId,
        chipSwitchId: a.chipSwitchId,
        slave: a.slave,
        button: a.button,
        executeValue: a.executeValue,
        label: a.label,
      };
    case "notification":
      return {
        type: "notification",
        message: a.notification,
        label: `notification ${a.notification}`,
      };
    case "delay": {
      const delayTime = formatSecondsToMMSS(a.delay);
      return {
        type: "delay",
        seconds: a.delay,
        delayTime,
        label: ` ${delayTime}`,
      };
    }
    case "scene":
      return {
        type: "scene",
        sceneId: a.id,
        sceneName: a.sceneName,
        label: a.label ,
      };
    default:
      return a;
  }
};

const resolveRepeatDayNumbers = (
  repeatType: number,
  customDays: number[],
): number[] => {
  switch (repeatType) {
    case 0: // Once
      return [];
    case 1: // Daily
      return ALL_DAY_NUMBERS;
    case 2: // Weekdays
      return WEEKDAY_NUMBERS;
    case 3: // Weekends
      return WEEKEND_NUMBERS;
    case 4: // Custom
      return customDays;
    default:
      return [];
  }
};

/* =========================================================
   COMPONENT
========================================================= */

const AutomationFormOffcanvas = ({
  isOpen: show,
  onClose,
  userId,
  selectedPremise,
  onSaved,
  editData,
}: Props) => {
  const [view, setView] = useState<View>("main");
  const [isAnySituation, setIsAnySituation] = useState(false);

  // Conditions and actions are intentionally stored separately.
  const [conditions, setConditions] = useState<any[]>([]);
  const [actions, setActions] = useState<any[]>([]);

  // The device selector is reused, but its result is saved to the
  // correct collection based on this mode.
  const [deviceMode, setDeviceMode] = useState<"condition" | "action">(
    "condition",
  );

  const [notificationMessage, setNotificationMessage] = useState("");

  const [delayTime, setDelayTime] = useState("00:05");
  const [sceneId, setSceneId] = useState("");
  const [sceneList, setSceneList] = useState<any[]>([]);
  const [loadingScenes, setLoadingScenes] = useState(false);

  const [devicesByCategory, setDevicesByCategory] = useState<
    Partial<Record<CategoryKey, DeviceRow[]>>
  >({});

  const [loadingDevices, setLoadingDevices] = useState(false);
  const [selectedItems, setSelectedItems] = useState<
    Record<string, SelectedDeviceItem>
  >({});

  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const [scheduleTime, setScheduleTime] = useState(getCurrentTimeString());
  const [scheduleType, setScheduleType] = useState(0);
  const [scheduleDelay, setScheduleDelay] = useState(0);
 

  // effective time period
  const [effectiveTimePeriodType, setEffectiveTimePeriodType] = useState(0); // 0 all day, 1 specific time
  const [effectiveTimePeriodStart, setEffectiveTimePeriodStart] =
    useState("09:00");
  const [effectiveTimePeriodEnd, setEffectiveTimePeriodEnd] =
    useState("18:00");

  // repeat
  const [effectiveRepeatType, setEffectiveRepeatType] = useState(1); // 0-4
  const [effectiveRepeatDayNumber, setEffectiveRepeatDayNumberState] =
    useState<number[]>([]);

  const premiseId = selectedPremise?.id ?? selectedPremise;
  const selectedRooms = useSelector(
    (state: any) => state.APIResponse.selectedRooms,
  );

  //create / edit
  const [automationName, setAutomationName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const isEditMode = !!editData?._id;

  const buildRoomId = () => {
    if (!selectedRooms || selectedRooms.length === 0) return "noroom";
    const ids = selectedRooms.map((r: any) =>
      typeof r === "string" ? r : r.id,
    );
    return [...ids].join(",");
  };

  const buildListPayload = () => ({
    draw: 1,
    order: [],
    start: 0,
    length: 1000,
    search: { value: "", regex: false },
    userId,
    premiseId,
    roomId: buildRoomId(),
  });

  const fetchAllCategories = async () => {
    setLoadingDevices(true);

    try {
      const categories = Object.keys(CATEGORY_ENDPOINTS) as CategoryKey[];

      const results = await Promise.all(
        categories.map((category) =>
          Request.post(
            `${url.BASE_URL}${CATEGORY_ENDPOINTS[category]}`,
            buildListPayload(),
          )
            .then((res) => {
              const rows = res.data?.data || [];
              return {
                category,
                rows: rows.map((row: any) => normalizeDevice(row, category)),
              };
            })
            .catch((error) => {
              console.error(`Failed to fetch ${category} list`, error);
              toast.error(
                `Failed to load ${CATEGORY_LABELS[category]} devices`,
              );
              return { category, rows: [] as DeviceRow[] };
            }),
        ),
      );

      const grouped: Partial<Record<CategoryKey, DeviceRow[]>> = {};
      results.forEach(({ category, rows }) => {
        grouped[category] = rows;
      });
      setDevicesByCategory(grouped);
    } finally {
      setLoadingDevices(false);
    }
  };

  const fetchSceneList = async () => {
    setLoadingScenes(true);

    try {
      const res = await Request.post(
        `${url.BASE_URL}${url.GET_SCENE_LIST}`,
        buildListPayload(),
      );

      const rows =
        res.data?.data ??
        res.data?.records ??
        res.data?.result?.data ??
        res.data?.result ??
        [];

      setSceneList(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error("Failed to fetch scene list", error);
      setSceneList([]);
      toast.error("Failed to load scenes");
    } finally {
      setLoadingScenes(false);
    }
  };

  const buildAutomationPayload = () => {
    const condition = conditions.map((c) => {
      switch (c.type) {
        case "device":
          return {
            type: "device",
            chipId: c.chipId,
            chipSwitchId: c.chipSwitchId,
            slave: c.slave,
            button: c.button,
            executeValue: c.executeValue,
            label: c.label,
          };
        case "schedule":
          return {
            type: "schedule",
            scheduleType: c.scheduleType,
            time: c.time,
            delay: c.delay,
            label: c.label,
          };
        case "location":
          return {
            type: "zone",
            location: c.location || "0,0",
            locationDisplayName: c.locationDisplayName || "",
            subType:
              c.subType || (c.direction === "leave" ? "outside" : "inside"),
            label: c.label,
          };
        case "environment":
          return {
            type: "environment",
            parameter: c.parameter,
            operator: c.operator,
            value: c.value,
            label: c.label,
          };
        default:
          return c;
      }
    });

    const action = actions.map((a) => {
      switch (a.type) {
        case "device":
          return {
            type: "chip",
            chipId: a.chipId,
            chipSwitchId: a.chipSwitchId,
            slave: a.slave,
            button: a.button,
            executeValue: a.executeValue,
            delay: 0,
            notification: "",
            label: a.label,
          };
        case "notification":
          return {
            type: "notification",
            delay: 0,
            notification: a.message,
          };
        case "delay":
          return {
            type: "delay",
            delay: a.seconds,
          };
        case "scene":
          return {
            type: "scene",
            delay: 0,
            sceneId: a.sceneId,
            label: a.label,
          };
        default:
          return a;
      }
    });

    return {
      userId,
      premiseId,
      name: automationName.trim(),
      icon: "automation",
      isAnySituation,
      effectiveTimePeriodType,
      effectiveTimePeriodStart:
        effectiveTimePeriodType === 1 ? effectiveTimePeriodStart : "",
      effectiveTimePeriodEnd:
        effectiveTimePeriodType === 1 ? effectiveTimePeriodEnd : "",
      effectiveRepeat: effectiveRepeatType,
      effectiveRepeatDayNumber: resolveRepeatDayNumbers(
        effectiveRepeatType,
        effectiveRepeatDayNumber,
      ),
      location: "",
      locationDisplayName: "",
      condition,
      action,
    };
  };

  const handleCreateAutomation = async () => {
    if (!automationName.trim()) {
      toast.error("Please enter an automation name");
      return;
    }
    if (conditions.length === 0) {
      toast.error("Please add at least one trigger condition");
      return;
    }
    if (actions.length === 0) {
      toast.error("Please add at least one action");
      return;
    }

    setIsCreating(true);
    try {
      const payload: any = buildAutomationPayload();

      if (isEditMode) {
        payload.automationId = editData._id;
      }

      await Request.post(`${url.BASE_URL}${url.CREATAE_AUTOMATION}`, payload);
      toast.success(
        isEditMode
          ? "Automation updated successfully"
          : "Automation created successfully",
      );
      handleClose(); // resets state and closes offcanvas
      onSaved();
    } catch (error) {
      console.error("Failed to save automation", error);
      toast.error(
        isEditMode
          ? "Failed to update automation"
          : "Failed to create automation",
      );
    } finally {
      setIsCreating(false);
    }
  };

  //  RESET
  const resetTriggerState = () => {
    setDevicesByCategory({});
    setSelectedItems({});
    setExpandedRows({});
    setConditions([]);
    setActions([]);
    setDeviceMode("condition");

    setNotificationMessage("");
    setSceneId("");
    setSceneList([]);

    setScheduleTime("00:00");
    setScheduleType(0);
    setScheduleDelay(0);

    setEffectiveTimePeriodType(0);
    setEffectiveTimePeriodStart("09:00");
    setEffectiveTimePeriodEnd("18:00");
    setEffectiveRepeatType(1);
    setEffectiveRepeatDayNumberState([]);
  };

  // HYDRATE FOR EDIT / RESET FOR CREATE, whenever the offcanvas opens
  useEffect(() => {
    if (!show) return;

    if (editData?._id) {
      setAutomationName(editData.name || "");
      setIsAnySituation(!!editData.isAnySituation);
      setConditions(
        (editData.condition || []).map(hydrateConditionFromStored),
      );
      setActions((editData.action || []).map(hydrateActionFromStored));

      setEffectiveTimePeriodType(editData.effectiveTimePeriodType ?? 0);
      setEffectiveTimePeriodStart(
        editData.effectiveTimePeriodStart || "09:00",
      );
      setEffectiveTimePeriodEnd(editData.effectiveTimePeriodEnd || "18:00");

      const repeatType = editData.effectiveRepeat ?? 1;
      setEffectiveRepeatType(repeatType);
      setEffectiveRepeatDayNumberState(
        repeatType === 4 ? editData.effectiveRepeatDayNumber || [] : [],
      );

      setView("main");
    } else {
      resetTriggerState();
      setAutomationName("");
      setView("main");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editData]);

  const clearSelection = () => {
    setSelectedItems({});
  };

  const toggleSelectedItem = (
    itemId: string,
    data: Omit<SelectedDeviceItem, "itemId">,
  ) => {
    setSelectedItems((prev) => {
      const next = { ...prev };

      if (next[itemId]) {
        delete next[itemId];
      } else {
        next[itemId] = {
          itemId,
          ...data,
        };
      }

      return next;
    });
  };

  const updateSelectedItemState = (itemId: string, updates: any) => {
    setSelectedItems((prev) => {
      const current = prev[itemId];

      if (!current) return prev;

      return {
        ...prev,
        [itemId]: {
          ...current,
          state: {
            ...current.state,
            ...updates,
          },
        },
      };
    });
  };

  const selectSwitchItem = (device: DeviceRow, sw: any, index: number) => {
    const itemId = String(sw?._id || sw?.id || `${device.id}-switch-${index}`);
    if (selectedItems[itemId]) {
      setSelectedItems((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      return;
    }

    toggleSelectedItem(itemId, {
      deviceId: device.id,
      device,
      category: "switch",
      state: {
        isOn: sw?.isOn ?? true,
        isDimmable: sw?.isDimmable ?? sw?.type === 2,
        dimmerValue: sw?.type === 0 ? "XX" : "1",
        category: "switch",
        switchType: sw?.type ?? 0,
        button: sw?.button,
        displayName:sw?.displayName,
        chipId: device.id,
        chipSwitchId: itemId,
        slave: device.raw?.slave,
      },
    });
  };

  const selectCurtainItem = (device: DeviceRow) => {
    const itemId = String(device.id);

    if (selectedItems[itemId]) {
      setSelectedItems((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      return;
    }

    toggleSelectedItem(itemId, {
      deviceId: device.id,
      device,
      category: "aircurtain",
      state: {
        isOn: true,
        isDimmable: true,
        dimmerValue: "00",
        executeValue: "01001",
        button: "01",
        curtainControl: "quick",
        category: "aircurtain",
        displayName: "Curtain",
        chipId: device.id,
        chipSwitchId: device.id,
        slave: device.raw?.slave,
      },
    });
  };

  const selectDriverItem = (device: DeviceRow, driver: any, index: number) => {
    const itemId = String(
      driver?._id || driver?.id || `${device.id}-driver-${index}`,
    );

    if (selectedItems[itemId]) {
      setSelectedItems((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      return;
    }

    toggleSelectedItem(itemId, {
      deviceId: device.id,
      device,
      category: "airdriver",
      state: {
        isOn: driver?.isOn ?? true,
        isDimmable: true,
        dimmerValue: "",
        brightness: driver?.brightness ?? AIR_DRIVER_DEFAULT_BRIGHTNESS,
        temperature: driver?.temp ?? AIR_DRIVER_DEFAULT_TEMP,
        category: "airdriver",
        displayName:driver?.displayName,
        chipId: device.id,
        chipSwitchId: itemId,
        slave: device.raw?.slave,
      },
    });
  };

  const selectLockItem = (device: DeviceRow) => {
    const itemId = String(device.id);

    if (selectedItems[itemId]) {
      setSelectedItems((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      return;
    }

    const raw = device.raw;

    toggleSelectedItem(itemId, {
      deviceId: device.id,
      device,
      category: "airlock",
      state: {
        isOn: raw?.isLocked ?? raw?.locked ?? raw?.isOn ?? true,
        isDimmable: false,
        dimmerValue: "XX",
        category: "airlock",
        chipId: device.id,
        chipSwitchId: device.id,
        slave: device.raw?.slave,
      },
    });
  };

  const updateDeviceState = (itemId: string, updates: any) => {
    updateSelectedItemState(itemId, updates);
  };

  const toggleDeviceOn = (itemId: string) => {
    const selected = selectedItems[itemId];

    if (!selected) return;

    updateSelectedItemState(itemId, {
      isOn: !selected.state.isOn,
    });
  };

  /* =========================================================
     MAIN NAVIGATION
  ========================================================= */

  const goToMain = () => setView("main");

  const handleClose = () => {
    resetTriggerState();
    setAutomationName("");
    setView("main");
    onClose();
  };

  const handleSelectType = (type: TriggerType) => {
    if (type === "device") {
      setDeviceMode("condition");
      clearSelection();
      setView("deviceList");
      void fetchAllCategories();
      return;
    }
    if (type === "schedule") {
      setView("schedule");
      return;
    }
    // if (type === "location") {
    //   setView("location");
    //   return;
    // }
    // if (type === "environment") {
    //   setView("environment");
    // }
  };

  const toggleGroup = (groupId: string) => {
    setExpandedRows((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  /* =========================================================
     BUILD / SAVE DEVICE CONDITIONS
  ========================================================= */

  const buildDeviceCondition = (item: SelectedDeviceItem) => {
    const state = item.state;
    let executeValue = "";
    let labelSuffix = "";
    if (item.category === "switch") {
      executeValue = state.isOn ? "XX1" : "XX0";
      labelSuffix = state.isOn ? "Turn on" : "Turn off";
    }

    if (item.category === "aircurtain") {
      executeValue = state.executeValue || "01001";
      labelSuffix =
        state.curtainControl === "precise"
          ? `Position ${parseInt(state.dimmerValue || "0", 16)}%`
          : state.isOn
          ? "Open"
          : "Close";
    }

    if (item.category === "airdriver") {
      executeValue =
        `B${state.brightness ?? AIR_DRIVER_DEFAULT_BRIGHTNESS}` +
        `T${state.temperature ?? AIR_DRIVER_DEFAULT_TEMP}`;
      labelSuffix =
        `${state.brightness ?? AIR_DRIVER_DEFAULT_BRIGHTNESS}% / ` +
        `${state.temperature ?? AIR_DRIVER_DEFAULT_TEMP}K`;
    }

    if (item.category === "airlock") {
      executeValue = state.isOn ? "R1" : "R0";
      labelSuffix = state.isOn ? "Locked" : "Unlocked";
    }

    return {
      type: "device",
      chipId: state.chipId,
      chipSwitchId: state.chipSwitchId,
      slave: state.slave,
      button: state.button,
      itemId: item.itemId,
      category: item.category,
      executeValue,
      label: `${labelSuffix} ${state.displayName} `,
    };
  };

  const buildDeviceAction = (item: SelectedDeviceItem) => {
    const state = item.state;
    let executeValue = "";
    let labelSuffix = "";

    if (item.category === "switch") {
      executeValue = state.isOn ? "XX1" : "XX0";
      labelSuffix = state.isOn ? "Turn on" : "Turn off";
    }

    if (item.category === "aircurtain") {
      executeValue = state.executeValue || "01001";
      labelSuffix =
        state.curtainControl === "precise"
          ? `Position ${parseInt(state.dimmerValue || "0", 16)}%`
          : state.isOn
          ? "Open"
          : "Close";
    }

    if (item.category === "airdriver") {
      executeValue =
        `B${state.brightness ?? AIR_DRIVER_DEFAULT_BRIGHTNESS}` +
        `T${state.temperature ?? AIR_DRIVER_DEFAULT_TEMP}`;
      labelSuffix =
        `${state.brightness ?? AIR_DRIVER_DEFAULT_BRIGHTNESS}% / ` +
        `${state.temperature ?? AIR_DRIVER_DEFAULT_TEMP}K`;
    }

    if (item.category === "airlock") {
      executeValue = state.isOn ? "R1" : "R0";
      labelSuffix = state.isOn ? "Locked" : "Unlocked";
    }

    return {
      type: "device",
      chipId: state.chipId,
      chipSwitchId: state.chipSwitchId,
      slave: state.slave,
      button: state.button,
      delay: 0,
      notification: "",
      itemId: item.itemId,
      category: item.category,
      executeValue,
      device: item.device,
      state: item.state,
      label: `${labelSuffix} ${state.displayName} `,
    };
  };

  const handleSaveDevice = (mode: "condition" | "action" = deviceMode) => {
    const items = Object.values(selectedItems);

    if (items.length === 0) {
      toast.error("Please select at least one device");
      return;
    }

    if (mode === "condition") {
      const newConditions = items.map(buildDeviceCondition);
      setConditions((prev) => [...prev, ...newConditions]);
    } else {
      const newActions = items.map(buildDeviceAction);
      setActions((prev) => [...prev, ...newActions]);
    }

    clearSelection();
    goToMain();
  };

  const handleSaveNotification = () => {
    const message = notificationMessage.trim();

    if (!message) {
      toast.error("Please enter notification message");
      return;
    }

    setActions((prev) => [
      ...prev,
      {
        type: "notification",
        message,
        label: `Notify: ${message}`,
      },
    ]);

    setNotificationMessage("");
    goToMain();
  };

  const handleSaveDelay = () => {
    const [minutes, seconds] = delayTime.split(":").map(Number);

    if (
      !Number.isFinite(minutes) ||
      !Number.isFinite(seconds) ||
      minutes < 0 ||
      seconds < 0 ||
      seconds > 59
    ) {
      toast.error("Please enter a valid time in MM:SS format");
      return;
    }

    const totalSeconds = minutes * 60 + seconds;

    if (totalSeconds < 1) {
      toast.error("Delay must be at least 1 second");
      return;
    }

    setActions((prev) => [
      ...prev,
      {
        type: "delay",
        seconds: totalSeconds,
        delayTime,
        label: `Delay ${delayTime}`,
      },
    ]);

    goToMain();
  };

  const handleSaveScene = () => {
    const id = sceneId.trim();

    if (!id) {
      toast.error("Please select a scene");
      return;
    }

    const selectedScene = sceneList.find((scene: any) => {
      const currentId = String(
        scene?.sceneId ?? scene?.id ?? scene?._id ?? scene?.scene_id ?? "",
      );

      return currentId === id;
    });

    const name = String(
      selectedScene?.sceneName ??
        selectedScene?.name ??
        selectedScene?.title ??
        "Scene",
    );

    setActions((prev) => [
      ...prev,
      {
        type: "scene",
        sceneId: id,
        sceneName: name,
        label: ` ${name}`,
      },
    ]);

    setSceneId("");
    setSceneList([]);
    goToMain();
  };

  //  SCHEDULE / LOCATION / ENVIRONMENT

  const handleSaveSchedule = () => {
    if (scheduleType === 0 && !scheduleTime) {
      toast.error("Please select a time");
      return;
    }

    setConditions((prev) => [
      ...prev,
      {
        type: "schedule",
        scheduleType,
        time: scheduleTime, // "HH:MM" — no ":00" appended
        location: "",
        locationDisplayName: "",
        delay: scheduleDelay,
        label:
          scheduleType === 0
            ? ` ${scheduleTime}`
            : scheduleType === 1
            ? "At sunrise"
            : "At sunset",
      },
    ]);
    goToMain();
  };


  const removeCondition = (index: number) => {
    setConditions((prev) => prev.filter((_, i) => i !== index));
  };

  const removeAction = (index: number) => {
    setActions((prev) => prev.filter((_, i) => i !== index));
  };

    //  EFFECTIVE TIME PERIOD

  const toggleCustomDay = (dayNumber: number) => {
    setEffectiveRepeatDayNumberState((prev) =>
      prev.includes(dayNumber)
        ? prev.filter((d) => d !== dayNumber)
        : [...prev, dayNumber].sort((a, b) => a - b),
    );
  };

  const handleSaveEffectiveTime = () => {
    if (effectiveTimePeriodType === 1) {
      if (!effectiveTimePeriodStart || !effectiveTimePeriodEnd) {
        toast.error("Please select start and end time");
        return;
      }
    }

    if (effectiveRepeatType === 4 && effectiveRepeatDayNumber.length === 0) {
      toast.error("Please select at least one day");
      return;
    }

    goToMain();
  };

  const goBack = () => {
    if (view === "deviceList") {
      clearSelection();
      setView(deviceMode === "action" ? "actionType" : "triggerType");
      return;
    }

    // if (view === "schedule" || view === "location" || view === "environment") {
    //   setView("triggerType");
    //   return;
    // }

    if (view === "notification" || view === "delay" || view === "scene") {
      setView("actionType");
      return;
    }

    if (view === "effectiveTime") {
      goToMain();
      return;
    }

    if (view === "actionType" || view === "triggerType") {
      goToMain();
    }
  };

  /* =========================================================
     RENDER: SWITCH SUB-ITEMS
  ========================================================= */

  const renderSwitches = (modelDevices: DeviceRow[]) => {
    const items = modelDevices.flatMap((device) =>
      (device.switches || []).map((sw: any, index: number) => ({
        device,
        sw,
        index,
      })),
    );

    if (items.length === 0) {
      return <div className="p-3 text-muted small">No switches found</div>;
    }

    return items.map(({ device, sw, index }) => {
      const itemId = String(
        sw?._id || sw?.id || `${device.id}-switch-${index}`,
      );
      const selected = selectedItems[itemId];
      const checked = !!selected;
      const thisSwitchType = sw?.type ?? 0;

      return (
        <div key={itemId} className="border-bottom py-2 px-3">
          <div className="d-flex align-items-center gap-2">
            <Input
              type="checkbox"
              checked={checked}
              onChange={() => selectSwitchItem(device, sw, index)}
              style={{ width: 14, height: 14, marginTop: 0 }}
            />

            <div className="flex-grow-1" style={{ fontSize: 13 }}>
              {sw?.displayName || `Switch ${sw?.button ?? ""}`.trim()}
            </div>

            {checked && (
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={selected?.state?.isOn ?? false}
                  onChange={() => toggleDeviceOn(itemId)}
                />
              </div>
            )}
          </div>

          {checked && selected && (
            <DeviceControl
              category="switch"
              itemId={itemId}
              selected={selected.state}
              switchType={thisSwitchType}
              onUpdate={updateDeviceState}
            />
          )}
        </div>
      );
    });
  };

  /* =========================================================
     RENDER: CURTAIN ITEMS (the device itself is the item)
  ========================================================= */

  const renderCurtains = (modelDevices: DeviceRow[]) => {
    if (modelDevices.length === 0) {
      return <div className="p-3 text-muted small">No curtains found</div>;
    }

    return modelDevices.map((device) => {
      const selected = selectedItems[device.id];
      const checked = !!selected;

      return (
        <div key={device.id} className="border-bottom py-2 px-3">
          <div className="d-flex align-items-center gap-2">
            <Input
              type="checkbox"
              checked={checked}
              onChange={() => selectCurtainItem(device)}
              style={{ width: 14, height: 14, marginTop: 0 }}
            />

            <div className="flex-grow-1" style={{ fontSize: 13 }}>
              {device.name}
            </div>

            {checked && (
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={selected?.state?.isOn ?? false}
                  onChange={() => toggleDeviceOn(device.id)}
                />
              </div>
            )}
          </div>

          {checked && selected && (
            <DeviceControl
              category="aircurtain"
              itemId={device.id}
              selected={selected.state}
              onUpdate={updateDeviceState}
            />
          )}
        </div>
      );
    });
  };

  /* =========================================================
     RENDER: DRIVER SUB-ITEMS
  ========================================================= */

  const renderDrivers = (modelDevices: DeviceRow[]) => {
    const items = modelDevices.flatMap((device) =>
      (device.drivers || []).map((driver: any, index: number) => ({
        device,
        driver,
        index,
      })),
    );

    if (items.length === 0) {
      return <div className="p-3 text-muted small">No drivers found</div>;
    }

    return items.map(({ device, driver, index }) => {
      const itemId = String(
        driver?._id || driver?.id || `${device.id}-driver-${index}`,
      );
      const selected = selectedItems[itemId];
      const checked = !!selected;

      return (
        <div key={itemId} className="border-bottom py-2 px-3">
          <div className="d-flex align-items-center gap-2">
            <Input
              type="checkbox"
              checked={checked}
              onChange={() => selectDriverItem(device, driver, index)}
              style={{ width: 14, height: 14, marginTop: 0 }}
            />

            <div className="flex-grow-1" style={{ fontSize: 13 }}>
              {driver?.name || "Light"}
            </div>

            {checked && (
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={selected?.state?.isOn ?? false}
                  onChange={() => toggleDeviceOn(itemId)}
                />
              </div>
            )}
          </div>

          {checked && selected && (
            <DeviceControl
              category="airdriver"
              itemId={itemId}
              selected={selected.state}
              onUpdate={updateDeviceState}
            />
          )}
        </div>
      );
    });
  };

  /* =========================================================
     RENDER: LOCK ITEMS (device itself is the item — not
     covered by DeviceControl, kept as a small inline toggle)
  ========================================================= */

  const renderLocks = (modelDevices: DeviceRow[]) => {
    if (modelDevices.length === 0) {
      return <div className="p-3 text-muted small">No locks found</div>;
    }

    return modelDevices.map((device) => {
      const selected = selectedItems[device.id];
      const checked = !!selected;

      return (
        <div key={device.id} className="border-bottom py-2 px-3">
          <div className="d-flex align-items-center gap-2">
            <Input
              type="checkbox"
              checked={checked}
              onChange={() => selectLockItem(device)}
              style={{ width: 14, height: 14, marginTop: 0 }}
            />

            <div className="flex-grow-1" style={{ fontSize: 13 }}>
              {device.name}
            </div>
          </div>

          {checked && (
            <div className="mt-2 ms-4 d-flex gap-2">
              <button
                type="button"
                className={`btn btn-sm ${
                  selected?.state?.isOn ? "btn-primary" : "btn-light border"
                }`}
                onClick={() =>
                  updateSelectedItemState(device.id, { isOn: true })
                }
              >
                Locked
              </button>
              <button
                type="button"
                className={`btn btn-sm ${
                  !selected?.state?.isOn ? "btn-primary" : "btn-light border"
                }`}
                onClick={() =>
                  updateSelectedItemState(device.id, { isOn: false })
                }
              >
                Unlocked
              </button>
            </div>
          )}
        </div>
      );
    });
  };

  /* =========================================================
     RENDER ONE CATEGORY — same room -> modelType_id grouping
     as CreateSceneOffcanvas
  ========================================================= */

  const renderCategory = (category: CategoryKey) => {
    const devices = devicesByCategory[category] || [];
    const grouped = groupDevicesByRoomAndModel(devices);

    return (
      <div key={category} className="mb-4">
        <div
          className="text-uppercase text-muted fw-semibold mb-1"
          style={{ fontSize: 11 }}
        >
          {CATEGORY_LABELS[category]}
        </div>

        {devices.length === 0 ? (
          <div className="text-muted py-2" style={{ fontSize: 12 }}>
            No {CATEGORY_LABELS[category].toLowerCase()} devices found
          </div>
        ) : (
          Object.entries(grouped).map(([roomName, models]) => (
            <div key={`${category}-${roomName}`} className="mb-3">
              <div className="fw-semibold mb-2" style={{ fontSize: 15 }}>
                {roomName}
              </div>

              {Object.entries(models).map(([uniqueKey, modelDevices]) => {
                const firstDevice = modelDevices[0];
                const groupId = buildGroupId(category, roomName, uniqueKey);
                const isExpanded = !!expandedRows[groupId];

                const deviceModelType =
                  firstDevice?.modelType?.trim() ||
                  firstDevice?.type?.trim() ||
                  "Unknown Type";

                const hasItems =
                  category === "switch"
                    ? modelDevices.some((d) => (d.switches || []).length > 0)
                    : category === "airdriver"
                    ? modelDevices.some((d) => (d.drivers || []).length > 0)
                    : true; // aircurtain / airlock: device itself is the item

                return (
                  <div
                    key={groupId}
                    className="border rounded-3 mb-2 overflow-hidden bg-white"
                  >
                    <div
                      className="d-flex align-items-center gap-2 px-3 py-2"
                      style={{
                        minHeight: 48,
                        cursor: hasItems ? "pointer" : "default",
                      }}
                      onClick={() => hasItems && toggleGroup(groupId)}
                    >
                      {hasItems ? (
                        <i
                          className={`bx ${
                            isExpanded ? "bx-chevron-down" : "bx-chevron-right"
                          }`}
                          style={{ fontSize: 17 }}
                        />
                      ) : (
                        <span style={{ width: 17 }} />
                      )}

                      <div className="flex-grow-1">
                        <div className="fw-semibold" style={{ fontSize: 14 }}>
                          {deviceModelType}
                        </div>
                      </div>

                      {firstDevice?.isOnline !== undefined && (
                        <span
                          className={`badge ${
                            firstDevice.isOnline
                              ? "badge-soft-success"
                              : "badge-soft-danger"
                          }`}
                          style={{ fontSize: 10 }}
                        >
                          {firstDevice.isOnline ? "online" : "offline"}
                        </span>
                      )}
                    </div>

                    {isExpanded && (
                      <div className="border-top">
                        {category === "switch" && renderSwitches(modelDevices)}
                        {category === "aircurtain" &&
                          renderCurtains(modelDevices)}
                        {category === "airdriver" &&
                          renderDrivers(modelDevices)}
                        {category === "airlock" && renderLocks(modelDevices)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    );
  };

  /* =========================================================
     UI
  ========================================================= */

  const headerTitle =
    view === "main"
      ? isEditMode
        ? "Edit Automation"
        : "Create Automation"
      : VIEW_TITLE[view];

  return (
    <Offcanvas
      isOpen={show}
      toggle={handleClose}
      direction="end"
      style={{ width: 420 }}
    >
      <OffcanvasHeader
        toggle={handleClose}
        className="d-flex align-items-center justify-content-between "
      >
        <div className="d-flex align-items-center gap-2 ">
          {view !== "main" && (
            <i
              className="bx bx-arrow-back"
              style={{ cursor: "pointer", fontSize: 18 }}
              onClick={goBack}
            />
          )}
          <h5 className="mb-0 fw-bold fs-3 ">{headerTitle}</h5>
        </div>
      </OffcanvasHeader>

      <OffcanvasBody className="d-flex flex-column p-0 mt-3 mx-3 ">
        {view === "main" && (
          <form className="d-flex flex-column h-100">
            <FormGroup className="mb-3">
              <Label className="fw-semibold" style={{ fontSize: 13 }}>
                Automation name
              </Label>
              <Input
                placeholder="Automation "
                value={automationName}
                onChange={(e) => setAutomationName(e.target.value)}
              />
            </FormGroup>
            <div className="d-flex gap-2 flex-column">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="situationMode"
                  id="anySituation"
                  checked={isAnySituation}
                  onChange={() => setIsAnySituation(true)}
                />
                <label className="form-check-label" htmlFor="anySituation">
                  when any of the situations occurs
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="situationMode"
                  id="allSituation"
                  checked={!isAnySituation}
                  onChange={() => setIsAnySituation(false)}
                />
                <label className="form-check-label" htmlFor="allSituation">
                  when all situation occurs
                </label>
              </div>
            </div>

            {conditions.length > 0 && (
              <div className="d-flex flex-column gap-2 mt-3">
                {conditions.map((c, i) => (
                  <div
                    key={i}
                    className="d-flex align-items-center justify-content-between border rounded-3 p-2"
                  >
                    <span style={{ fontSize: 13 }}>{c.label}</span>
                    <i
                      className="bx bx-x"
                      style={{ cursor: "pointer" }}
                      onClick={() => removeCondition(i)}
                    />
                  </div>
                ))}
              </div>
            )}

            <Button
              type="button"
              color="dark"
              className="d-flex align-items-center gap-1 p-2 fw-bold rounded-3 mt-4 justify-content-center "
              onClick={(e) => {
                e.preventDefault();
                setView("triggerType");
              }}
            >
              <i className="bx bx-plus" style={{ fontSize: 16 }} />
              Add a trigger condition
            </Button>

            <div className="fs-4 mt-4 fw-bolder">
              <p className="mb-0">Adjust the device to</p>
            </div>

            {actions.length > 0 && (
              <div className="d-flex flex-column gap-2 mt-3">
                {actions.map((action, i) => (
                  <div
                    key={`${action.type}-${i}`}
                    className="d-flex align-items-center justify-content-between border rounded-3 p-2"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <i
                        className={`bx ${
                          action.type === "device"
                            ? "bx-devices"
                            : action.type === "notification"
                            ? "bx-bell"
                            : action.type === "delay"
                            ? "bx-time-five"
                            : "bx-layer"
                        }`}
                      />
                      <span style={{ fontSize: 13 }}>{action.label}</span>
                    </div>

                    <i
                      className="bx bx-x"
                      style={{ cursor: "pointer" }}
                      onClick={() => removeAction(i)}
                    />
                  </div>
                ))}
              </div>
            )}

            <Button
              type="button"
              color="dark"
              className="d-flex align-items-center gap-1 p-2 fw-bold rounded-3 mt-4 justify-content-center"
              onClick={() => setView("actionType")}
            >
              <i className="bx bx-plus" style={{ fontSize: 16 }} />
              Add action
            </Button>

            <div className="d-flex flex-column mt-auto mb-3">
              <Button
                type="button"
                className="mb-3 bg-gray-200 d-flex align-items-center justify-content-between"
                onClick={() => setView("effectiveTime")}
              >
                <span>Effective time period</span>
                <span className="" style={{ fontSize: 12 }}>
                  {effectiveTimePeriodType === 0
                    ? "All day"
                    : `${effectiveTimePeriodStart} - ${effectiveTimePeriodEnd}`}
                  {" • "}
                  {REPEAT_TYPE_LABELS[effectiveRepeatType]}
                </span>
              </Button>
              <Button
                className="bg-white text-black fw-bold flex-fill"
                onClick={handleCreateAutomation}
                disabled={isCreating}
              >
                {isCreating ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {isEditMode ? "Updating..." : "Creating..."}
                  </>
                ) : isEditMode ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </form>
        )}

        {view === "triggerType" && (
          <div className="d-flex flex-column gap-2">
            <button
              type="button"
              className="btn btn-light border d-flex align-items-center gap-2 p-3 rounded-3 text-start"
              onClick={() => handleSelectType("device")}
            >
              <i className="bx bx-devices" />
              <span className="fw-semibold">Device</span>
              <i className="bx bx-chevron-right ms-auto" />
            </button>

            <button
              type="button"
              className="btn btn-light border d-flex align-items-center gap-2 p-3 rounded-3 text-start"
              onClick={() => handleSelectType("schedule")}
            >
              <i className="bx bx-time-five" />
              <span className="fw-semibold">Schedule</span>
              <i className="bx bx-chevron-right ms-auto" />
            </button>

            {/* <button
              type="button"
              className="btn btn-light border d-flex align-items-center gap-2 p-3 rounded-3 text-start"
              onClick={() => handleSelectType("location")}
            >
              <i className="bx bx-map-pin" />
              <span className="fw-semibold">Arrive / Leave</span>
              <i className="bx bx-chevron-right ms-auto" />
            </button>

            <button
              type="button"
              className="btn btn-light border d-flex align-items-center gap-2 p-3 rounded-3 text-start"
              onClick={() => handleSelectType("environment")}
            >
              <i className="bx bx-cloud" />
              <span className="fw-semibold">Outdoor Environment</span>
              <i className="bx bx-chevron-right ms-auto" />
            </button> */}
          </div>
        )}

        {view === "actionType" && (
          <div className="d-flex flex-column gap-2">
            <button
              type="button"
              className="btn btn-light border d-flex align-items-center gap-2 p-3 rounded-3 text-start"
              onClick={() => {
                setDeviceMode("action");
                clearSelection();
                setView("deviceList");
                void fetchAllCategories();
              }}
            >
              <i className="bx bx-devices" />
              <span className="fw-semibold">Device</span>
              <i className="bx bx-chevron-right ms-auto" />
            </button>

            <button
              type="button"
              className="btn btn-light border d-flex align-items-center gap-2 p-3 rounded-3 text-start"
              onClick={() => setView("notification")}
            >
              <i className="bx bx-bell" />
              <span className="fw-semibold">Notification</span>
              <i className="bx bx-chevron-right ms-auto" />
            </button>

            <button
              type="button"
              className="btn btn-light border d-flex align-items-center gap-2 p-3 rounded-3 text-start"
              onClick={() => setView("delay")}
            >
              <i className="bx bx-time-five" />
              <span className="fw-semibold">Delay</span>
              <i className="bx bx-chevron-right ms-auto" />
            </button>

            <button
              type="button"
              className="btn btn-light border d-flex align-items-center gap-2 p-3 rounded-3 text-start"
              onClick={() => {
                setSceneId("");
                setView("scene");
                void fetchSceneList();
              }}
            >
              <i className="bx bx-layer" />
              <span className="fw-semibold">Scene</span>
              <i className="bx bx-chevron-right ms-auto" />
            </button>
          </div>
        )}

        {view === "deviceList" && (
          <div className="d-flex flex-column h-100">
            <div
              className="flex-grow-1"
              style={{ overflowY: "auto", paddingBottom: 12 }}
            >
              {loadingDevices ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <Spinner size="sm" />
                  <span className="ms-2">Loading all devices...</span>
                </div>
              ) : (
                CATEGORY_KEYS.map((category) => renderCategory(category))
              )}
            </div>

            <div className="d-flex gap-2 mt-auto mb-3">
              <Button
                color="light"
                className="border fw-bold rounded-3 flex-fill"
                onClick={() => {
                  clearSelection();
                  setView(
                    deviceMode === "action" ? "actionType" : "triggerType",
                  );
                }}
              >
                Cancel
              </Button>

              <Button
                color="dark"
                className="fw-bold rounded-3 flex-fill"
                onClick={() => handleSaveDevice(deviceMode)}
                disabled={Object.keys(selectedItems).length === 0}
              >
                Save
              </Button>
            </div>
          </div>
        )}

        {view === "notification" && (
          <div className="d-flex flex-column h-100">
            <div className="d-flex flex-column gap-3">
              <FormGroupBlock label="Notification message">
                <Input
                  type="textarea"
                  rows={5}
                  placeholder="Enter notification message"
                  value={notificationMessage}
                  onChange={(e) => setNotificationMessage(e.target.value)}
                />
              </FormGroupBlock>
            </div>

            <div className="d-flex gap-2 mt-auto mb-3">
              <Button
                type="button"
                color="light"
                className="border fw-bold rounded-3 flex-fill"
                onClick={() => {
                  setNotificationMessage("");
                  setView("actionType");
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                color="dark"
                className="fw-bold rounded-3 flex-fill"
                onClick={handleSaveNotification}
              >
                Save
              </Button>
            </div>
          </div>
        )}

        {view === "delay" && (
          <div className="d-flex flex-column h-100">
            <div className="d-flex flex-column gap-3">
              <FormGroupBlock label="Delay time">
                <Input
                  type="text"
                  value={delayTime}
                  placeholder="MM:SS"
                  maxLength={5}
                  inputMode="numeric"
                  step={1}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, "");

                    if (value.length > 4) {
                      value = value.slice(0, 4);
                    }

                    if (value.length >= 3) {
                      value = `${value.slice(0, 2)}:${value.slice(2)}`;
                    }

                    setDelayTime(value);
                  }}
                />
              </FormGroupBlock>
            </div>

            <div className="d-flex gap-2 mt-auto mb-3">
              <Button
                type="button"
                color="light"
                className="border fw-bold rounded-3 flex-fill"
                onClick={() => {
                  setView("actionType");
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                color="dark"
                className="fw-bold rounded-3 flex-fill"
                onClick={handleSaveDelay}
              >
                Save
              </Button>
            </div>
          </div>
        )}

        {view === "scene" && (
          <div className="d-flex flex-column justify-content-end  ">
            <div
              className="d-flex flex-column gap-3 mb-2 "
              style={{
                maxHeight: "600px",
                overflowY: "auto",
              }}
            >
              <FormGroupBlock label="Select Scene">
                {loadingScenes ? (
                  <div className="d-flex align-items-center border rounded-3 px-3 py-2">
                    <Spinner size="sm" />
                    <span className="ms-2">Loading scenes...</span>
                  </div>
                ) : sceneList.length === 0 ? (
                  <div className="text-muted border rounded-3 px-3 py-3">
                    No scenes available
                  </div>
                ) : (
                  <div className="border rounded-3 overflow-hidden">
                    {sceneList.map((scene: any) => {
                      const id = String(scene?.id || "");
                      const name = String(scene?.name || "");

                      if (!id) return null;

                      return (
                        <div
                          key={id}
                          className="d-flex align-items-center px-3 py-2 border-bottom"
                          style={{
                            cursor: "pointer",
                          }}
                          onClick={() => setSceneId(id)}
                        >
                          <Input
                            type="checkbox"
                            className="m-0 me-2"
                            checked={sceneId === id}
                            onChange={() => setSceneId(id)}
                            onClick={(e) => e.stopPropagation()}
                          />

                          <span className="text-dark">{name}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </FormGroupBlock>
              {!loadingScenes && sceneList.length === 0 && (
                <div className="text-muted small">No scenes found</div>
              )}
            </div>

            <div className="d-flex gap-2  ">
              <Button
                type="button"
                color="light"
                className="border fw-bold rounded-3 flex-fill"
                onClick={() => {
                  setSceneId("");
                  setSceneList([]);
                  setView("actionType");
                }}
              >
                Cancel
              </Button>

              <Button
                type="button"
                color="dark"
                className="fw-bold rounded-3 flex-fill"
                onClick={handleSaveScene}
                disabled={!sceneId || loadingScenes}
              >
                Save
              </Button>
            </div>
          </div>
        )}

        {view === "schedule" && (
          <div className="d-flex flex-column h-100">
            <div className="d-flex flex-column gap-3">
              <FormGroupBlock label="Schedule type">
                <Input
                  type="select"
                  value={scheduleType}
                  onChange={(e) => setScheduleType(Number(e.target.value))}
                >
                  <option value={0}>Custom Time</option>
                  {/* <option value={1}>Sunrise</option>
                  <option value={2}>Sunset</option> */}
                </Input>
              </FormGroupBlock>

              {scheduleType === 0 && (
                <FormGroupBlock label="Time">
                  <Input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                  />
                </FormGroupBlock>
              )}
            </div>

            <div className="d-flex gap-2 mt-auto mb-3">
              <Button
                color="light"
                className="border fw-bold rounded-3 flex-fill"
                onClick={goToMain}
              >
                Cancel
              </Button>
              <Button
                color="dark"
                className="fw-bold rounded-3 flex-fill"
                onClick={handleSaveSchedule}
              >
                Save
              </Button>
            </div>
          </div>
        )}

        {/* {view === "location" && (
          <div className="d-flex flex-column h-100">
            <div className="d-flex gap-2">
              <button
                type="button"
                className={`btn btn-sm ${
                  locationDirection === "arrive"
                    ? "btn-primary"
                    : "btn-light border"
                }`}
                onClick={() => setLocationDirection("arrive")}
              >
                Arrive
              </button>

              <button
                type="button"
                className={`btn btn-sm ${
                  locationDirection === "leave"
                    ? "btn-primary"
                    : "btn-light border"
                }`}
                onClick={() => setLocationDirection("leave")}
              >
                Leave
              </button>
            </div>

            <div className="d-flex gap-2 mt-auto mb-3">
              <Button
                color="light"
                className="border fw-bold rounded-3 flex-fill"
                onClick={goToMain}
              >
                Cancel
              </Button>
              <Button
                color="dark"
                className="fw-bold rounded-3 flex-fill"
                onClick={handleSaveLocation}
              >
                Save
              </Button>
            </div>
          </div>
        )}

        {view === "environment" && (
          <div className="d-flex flex-column h-100">
            <div className="d-flex flex-column gap-3">
              <FormGroupBlock label="Parameter">
                <Input
                  type="select"
                  value={envParam}
                  onChange={(e) => setEnvParam(e.target.value)}
                >
                  <option value="temperature">Temperature</option>
                  <option value="humidity">Humidity</option>
                  <option value="aqi">Air Quality</option>
                </Input>
              </FormGroupBlock>

              <FormGroupBlock label="Condition">
                <Input
                  type="select"
                  value={envOperator}
                  onChange={(e) => setEnvOperator(e.target.value)}
                >
                  <option value=">">Greater than</option>
                  <option value="<">Less than</option>
                  <option value="=">Equal to</option>
                </Input>
              </FormGroupBlock>

              <FormGroupBlock label="Value">
                <Input
                  value={envValue}
                  onChange={(e) => setEnvValue(e.target.value)}
                />
              </FormGroupBlock>
            </div>

            <div className="d-flex gap-2 mt-auto mb-3">
              <Button
                color="light"
                className="border fw-bold rounded-3 flex-fill"
                onClick={goToMain}
              >
                Cancel
              </Button>
              <Button
                color="dark"
                className="fw-bold rounded-3 flex-fill"
                onClick={handleSaveEnvironment}
              >
                Save
              </Button>
            </div>
          </div>
        )} */}

        {view === "effectiveTime" && (
          <div className="d-flex flex-column h-100">
            <div className="d-flex flex-column gap-4">
              {/* --- Time period type --- */}
              <FormGroupBlock label="Time period">
                <div className="d-flex gap-2 mt-1">
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      effectiveTimePeriodType === 0
                        ? "btn-primary"
                        : "btn-light border"
                    }`}
                    onClick={() => setEffectiveTimePeriodType(0)}
                  >
                    All day
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      effectiveTimePeriodType === 1
                        ? "btn-primary"
                        : "btn-light border"
                    }`}
                    onClick={() => setEffectiveTimePeriodType(1)}
                  >
                    Specific time period
                  </button>
                </div>
              </FormGroupBlock>

              {effectiveTimePeriodType === 1 && (
                <div className="d-flex gap-3">
                  <FormGroupBlock label="Start time">
                    <Input
                      type="time"
                      value={effectiveTimePeriodStart}
                      onChange={(e) =>
                        setEffectiveTimePeriodStart(e.target.value)
                      }
                    />
                  </FormGroupBlock>
                  <FormGroupBlock label="End time">
                    <Input
                      type="time"
                      value={effectiveTimePeriodEnd}
                      onChange={(e) =>
                        setEffectiveTimePeriodEnd(e.target.value)
                      }
                    />
                  </FormGroupBlock>
                </div>
              )}

              <hr className="my-0" />

              {/* --- Repeat --- */}
              <FormGroupBlock label="Repeat">
                <div className="d-flex flex-wrap gap-2 mt-1">
                  {([0, 1, 2, 3, 4] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={`btn btn-sm ${
                        effectiveRepeatType === type
                          ? "btn-primary"
                          : "btn-light border"
                      }`}
                      onClick={() => setEffectiveRepeatType(type)}
                    >
                      {REPEAT_TYPE_LABELS[type]}
                    </button>
                  ))}
                </div>
              </FormGroupBlock>

              {effectiveRepeatType === 4 && (
                <FormGroupBlock label="Select days">
                  <div className="d-flex flex-wrap gap-2 mt-1">
                    {WEEK_DAYS.map((day) => {
                      const checked = effectiveRepeatDayNumber.includes(
                        day.number,
                      );
                      return (
                        <button
                          key={day.number}
                          type="button"
                          className={`btn btn-sm rounded-circle ${
                            checked ? "btn-primary" : "btn-light border"
                          }`}
                          style={{ width: 40, height: 40, padding: 0 }}
                          onClick={() => toggleCustomDay(day.number)}
                        >
                          {day.short}
                        </button>
                      );
                    })}
                  </div>
                </FormGroupBlock>
              )}
            </div>

            <div className="d-flex gap-2 mt-auto mb-3">
              <Button
                type="button"
                color="light"
                className="border fw-bold rounded-3 flex-fill"
                onClick={goToMain}
              >
                Cancel
              </Button>
              <Button
                type="button"
                color="dark"
                className="fw-bold rounded-3 flex-fill"
                onClick={handleSaveEffectiveTime}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default AutomationFormOffcanvas;

